import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";
import { describeNetworkingError } from "services/ApiClient/describeNetworkingError";
import { useToast } from "@chakra-ui/react";
import { Message } from "features/Resources/Message/models";
import { apiMessageById, v2ApiNewHireMessagesPathById } from "helpers/url/api/v2";
import { NewHireJourneyId } from "models/newHire";

export const messageKeys = {
  all: ["messages"] as const,
  lists: () => [...messageKeys.all, "list"] as const,
  list: (filters: string) => [...messageKeys.lists(), { filters }] as const,
  details: () => [...messageKeys.all, "detail"] as const,
  detail: (id: string) => [...messageKeys.details(), id] as const,
  byNewHireJourneys: () => [...messageKeys.all, "newHireJourneys"] as const,
  byNewHireJourney: (id: string) => [...messageKeys.byNewHireJourneys(), id] as const,
};

export function setNewMessageInQueryClient(queryClient: QueryClient, newMessage: Message) {
  queryClient.setQueryData(messageKeys.detail(newMessage.id), (oldData: Message | undefined) => {
    if (!oldData) {
      return newMessage;
    }
    return {
      ...oldData,
      ...newMessage,
    };
  });
}

export const useMessageDetailQuery = (id: string | undefined, options = {}) => useQuery<Message | null>({
  queryKey: messageKeys.detail(id as string),
  queryFn: async () => apiClient.get(apiMessageById(id as string)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  enabled: !!id && ((options as { enabled?: boolean }).enabled ?? true),
  ...options,
});

export const useMessageListQuery = (queryString: string, options = {}) => {
  const query = queryString ? `?${queryString}` : "";
  return useQuery<Message[]>({
    queryKey: queryString ? messageKeys.list(queryString) : messageKeys.lists(),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.messages.base)}${query}`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });
};

export const useNewHireJourneyMessagesQuery = (newHireJourneyId: NewHireJourneyId, options = {}) => useQuery<Message[]>({
  queryKey: messageKeys.byNewHireJourney(newHireJourneyId),
  queryFn: async () => apiClient.get(v2ApiNewHireMessagesPathById(newHireJourneyId)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!newHireJourneyId && ((options as { enabled?: boolean }).enabled ?? true),
});

function useMessageRequest(mutationFn: (data: Message) => Promise<any>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      setNewMessageInQueryClient(queryClient, res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useUpdateMessage(messageId: string) {
  return useMessageRequest(async (data: Message) => apiClient.put<Message>(apiMessageById(messageId), { message: data }));
}

export function useCreateMessage() {
  return useMessageRequest(async (data: Message) => apiClient.post<Message>(API_ROUTES.messages.base, { message: data }));
}

export function useForceSendMessage(onSuccess?: (res: any) => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ messageId, senderId }: { messageId: string; senderId?: string | null }) => {
      const params = senderId !== undefined ? { sender_id: senderId } : {};
      return apiClient.post(`${apiMessageById(messageId)}/force`, params);
    },
    onSuccess: (res) => {
      if (res.data?.id) {
        setNewMessageInQueryClient(queryClient, res.data);
      }
      toast({
        title: "Message sent!",
        status: "success",
        duration: 3000,
      });
      onSuccess?.(res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: "Failed to send message",
        status: "error",
        duration: 5000,
      });
    },
  });
}
